// Keep prose comfortable to scan while code blocks and tables can use the
// width chosen in Appearance. Values are points, so narrow panes still shrink.
//
// `compact` picks the type scale: true is the dense Mac scale Work mode uses;
// false is the shared chat scale, so macOS chat matches web and mobile
// (17pt body and the matching heading hierarchy). Layout stays Mac-specific
// either way: same prose width, markers, blockquote and inline-code treatment.
import { scaleTextStyles } from '@/constants/macFontScale';

export function macMarkdownStyles(shared, { colors, isDark, isUser, isDestructive, compact = true, fontScale = 1 }) {
  const prose = { maxWidth: 680, width: '100%' };
  const type = compact
    ? { fontFamily: 'System', fontSize: 14, lineHeight: 21 }
    : { fontFamily: shared.text.fontFamily, fontSize: shared.text.fontSize, lineHeight: shared.text.lineHeight };
  const marker = {
    ...type,
    color: colors.text_color_secondary,
    minWidth: 20,
    flexShrink: 0,
    marginRight: 8,
    marginTop: 0,
    textAlign: 'right',
  };
  const styles = {
    ...shared,
    body: { ...shared.body, ...type },
    text: { ...shared.text, ...type },
    selectableText: { ...shared.selectableText, ...type },
    paragraph: { ...shared.paragraph, ...type, ...prose },
    paragraphNotFirst: { marginTop: 12 },
    strong: { fontWeight: '600' },
    bullet_list: { ...shared.bullet_list, ...prose, paddingLeft: 0, marginTop: 6, marginBottom: 6 },
    ordered_list: { ...shared.ordered_list, ...prose, paddingLeft: 0, marginTop: 6, marginBottom: 6 },
    list_item: { ...shared.list_item, marginTop: 4, marginBottom: 6 },
    bullet_list_icon: marker,
    ordered_list_icon: marker,
    blockquote: { ...shared.blockquote, ...prose, borderLeftWidth: 2, paddingLeft: 16 },
    code_inline: {
      ...shared.code_inline,
      // Native nested Text paints square background runs without CSS padding
      // or rounded corners. Monospace + a quiet tint avoids a wall of boxes.
      backgroundColor: 'transparent',
      color: isUser || isDestructive ? shared.text.color : (isDark ? '#B7C8DF' : '#334F72'),
      fontFamily: 'Menlo',
      fontSize: 13,
      fontWeight: '400',
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
  };
  if (compact) {
    const sizes = [22, 19, 16, 15, 14, 14];
    const heights = [29, 26, 23, 22, 21, 21];
    sizes.forEach((fontSize, index) => {
      const key = `heading${index + 1}`;
      styles[key] = {
        ...shared[key], ...prose, fontFamily: 'System', fontSize,
        lineHeight: heights[index], fontWeight: '600', letterSpacing: 0,
        marginTop: index < 3 ? 20 : 16, marginBottom: 8,
      };
    });
  } else {
    // Chat keeps the web/mobile heading sizes but still wraps at the Mac prose
    // width, so a 17pt body never sits under a smaller heading.
    for (let index = 0; index < 6; index += 1) {
      const key = `heading${index + 1}`;
      styles[key] = { ...shared[key], ...prose };
    }
  }
  return scaleTextStyles(styles, fontScale);
}
